import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import CommunicationNotifier from '../../components/ui/CommunicationNotifier';
import SharedToolsMenu from '../../components/ui/SharedToolsMenu';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClassCard from './components/ClassCard';
import TodaySchedule from './components/TodaySchedule';
import QuickStats from './components/QuickStats';
import PendingRequestsCard from './components/PendingRequestsCard';
import NotificationsCard from './components/NotificationsCard';
import AppointmentsCard from './components/AppointmentsCard';
import { Class, Message, QuickStat, TodayLesson } from './types';
import { useAuth } from '../../context/AuthContext';
import {
  type NotificationItem,
  type PendingRequest,
  type BackendClassroomStats,
  fetchMyClasses,
  fetchMyTodaySchedule,
  fetchNotifications,
  fetchPendingRequests,
  fetchClassroomStats,
  fetchStudentsInClass,
  markAllNotificationsRead,
} from '../../lib/teacherApi';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { me, signOut } = useAuth();
  const isB2B = (me?.school_mode || 'b2c') === 'b2b';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [classes, setClasses] = useState<Class[]>([]);
  const [schoolRating, setSchoolRating] = useState<{
    ratingPercent: number | null;
    ratingDelta: number | null;
    ratingTrend: 'up' | 'down' | 'flat' | null;
    ratingTrendWindowDays: number | null;
  } | null>(null);
  const [todayLessons, setTodayLessons] = useState<TodayLesson[]>([]);
  const [quickStats, setQuickStats] = useState<QuickStat[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const user = useMemo(() => {
    return {
      role: 'teacher' as const,
      name: me?.full_name || 'Profesor',
      avatar: me?.avatar_url || undefined,
    };
  }, [me?.avatar_url, me?.full_name]);

  const normalize = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const todayRo = useMemo(() => {
    const days = ['Duminica', 'Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri', 'Sambata'];
    return days[new Date().getDay()];
  }, []);

  const parseTimeToMinutes = (t: string): number | null => {
    const raw = t.trim();
    const m1 = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (m1) return parseInt(m1[1], 10) * 60 + parseInt(m1[2], 10);
    const m2 = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (m2) {
      let h = parseInt(m2[1], 10) % 12;
      const min = parseInt(m2[2], 10);
      if (m2[3].toUpperCase() === 'PM') h += 12;
      return h * 60 + min;
    }
    return null;
  };

  const loadDashboard = useCallback(async () => {
    if (!me) return;
    setError(null);
    try {
      const jsDay = new Date().getDay(); // 0=Sun
      const dayOfWeek = (jsDay + 6) % 7; // 0=Mon

      const [classesRaw, scheduleRaw, notifRaw, reqRaw] = await Promise.all([
        fetchMyClasses(),
        fetchMyTodaySchedule(dayOfWeek),
        fetchNotifications(),
        fetchPendingRequests(),
      ]);

      const classCounts = await Promise.all(
        classesRaw.map(async (c) => {
          try {
            const [students, stats] = await Promise.all([
              fetchStudentsInClass(c.id),
              fetchClassroomStats(c.id),
            ]);
            const school = stats?.school ?? null;
            return {
              id: c.id,
              name: c.name,
              studentCount: students.length,
              ratingPercent: stats?.rating_percent ?? null,
              ratingDelta: stats?.rating_delta ?? null,
              ratingTrend: stats?.rating_trend ?? null,
              ratingTrendWindowDays: stats?.rating_trend_window_days ?? null,
              topRank: (stats as BackendClassroomStats).top_rank ?? null,
              topMessage: (stats as BackendClassroomStats).top_message ?? null,
              schoolRatingPercent: school?.rating_percent ?? null,
              schoolRatingDelta: school?.rating_delta ?? null,
              schoolRatingTrend: school?.rating_trend ?? null,
              schoolRatingTrendWindowDays: school?.rating_trend_window_days ?? null,
            };
          } catch {
            return {
              id: c.id,
              name: c.name,
              studentCount: 0,
              ratingPercent: null,
              ratingDelta: null,
              ratingTrend: null,
              ratingTrendWindowDays: null,
              topRank: null,
              topMessage: null,
              schoolRatingPercent: null,
              schoolRatingDelta: null,
              schoolRatingTrend: null,
              schoolRatingTrendWindowDays: null,
            };
          }
        })
      );

      const schoolCandidate = classCounts.find((c: any) => typeof c?.schoolRatingPercent === 'number' || c?.schoolRatingTrend);
      setSchoolRating(
        schoolCandidate
          ? {
              ratingPercent: (schoolCandidate.schoolRatingPercent ?? null) as number | null,
              ratingDelta: (schoolCandidate.schoolRatingDelta ?? null) as number | null,
              ratingTrend: (schoolCandidate.schoolRatingTrend ?? null) as 'up' | 'down' | 'flat' | null,
              ratingTrendWindowDays: (schoolCandidate.schoolRatingTrendWindowDays ?? null) as number | null,
            }
          : null
      );

      const palette = ['#2563EB', '#059669', '#D97706', '#7C3AED', '#DC2626', '#0EA5E9'];
      const mappedClasses: Class[] = classCounts.map((c, idx) => ({
        id: String(c.id),
        name: c.name,
        studentCount: c.studentCount,
        color: palette[idx % palette.length],
        ratingPercent: c.ratingPercent ?? null,
        ratingDelta: c.ratingDelta ?? null,
        ratingTrend: c.ratingTrend ?? null,
        ratingTrendWindowDays: c.ratingTrendWindowDays ?? null,
        topRank: (c as any).topRank ?? null,
        topMessage: (c as any).topMessage ?? null,
      }));

      const unreadNotifications = notifRaw.filter((n) => !n.is_read).length;
      const totalStudents = classCounts.reduce((sum, c) => sum + c.studentCount, 0);

      const stats: QuickStat[] = [
        { label: 'Total elevi', value: totalStudents, change: 0, trend: 'neutral', icon: 'Users', color: '#2563EB' },
        { label: 'Clasele mele', value: mappedClasses.length, change: 0, trend: 'neutral', icon: 'School', color: '#7C3AED' },
        { label: 'Cereri în așteptare', value: reqRaw.length, change: 0, trend: 'neutral', icon: 'Clock', color: '#D97706' },
        { label: 'Notificări necitite', value: unreadNotifications, change: 0, trend: 'neutral', icon: 'Bell', color: '#059669' },
      ];

      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();

      const typeLabel = (t?: string | null) => {
        const tt = normalize(t || '');
        if (tt === 'lecture') return 'Curs';
        if (tt === 'lab') return 'Laborator';
        if (tt === 'tutorial') return 'Seminar';
        return t || null;
      };

      const todayLessonsMapped: TodayLesson[] = (scheduleRaw || [])
        .map((l) => {
          const id = String(l.id);
          const startMin = parseTimeToMinutes(l.start_time);
          const endMin = parseTimeToMinutes(l.end_time);
          let status: TodayLesson['status'] = 'upcoming';
          if (startMin !== null && endMin !== null) {
            if (nowMin >= endMin) status = 'completed';
            else if (nowMin >= startMin) status = 'in-progress';
          }
          return {
            id,
            subject: l.subject || '—',
            start: l.start_time,
            end: l.end_time,
            day: todayRo,
            room: (l as any).room ?? null,
            classroomId: (l as any).classroom_id ?? null,
            classroomName: l.classroom_name || null,
            timetableId: (l as any).timetable_id ?? null,
            timetableName: l.timetable_name || null,
            type: typeLabel(l.lesson_type),
            comment: l.comment,
            status,
          };
        })
        .sort((a, b) => (parseTimeToMinutes(a.start) ?? 0) - (parseTimeToMinutes(b.start) ?? 0));

      const notifAsMessages: Message[] = notifRaw.slice(0, 10).map((n) => ({
        id: String(n.id),
        sender: 'System',
        senderRole: 'admin',
        subject: n.title,
        preview: n.content,
        timestamp: new Date(n.created_at).toISOString(),
        isRead: Boolean(n.is_read),
        priority: n.is_read ? 'low' : 'normal',
      }));

      setClasses(mappedClasses);
      setTodayLessons(todayLessonsMapped);
      setQuickStats(stats);
      setPendingRequests(reqRaw);
      setNotifications(notifRaw);
      setMessages(notifAsMessages);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load dashboard data.');
    }
  }, [me, normalize, parseTimeToMinutes, todayRo]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>Panou profesor - Scola</title>
        <meta
          name="description"
          content="Gestionează clasele, orarul și comunicarea cu elevii din panoul profesorului."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header
          user={user}
          notificationCount={notifications.filter((n) => !n.is_read).length}
          onNavigate={handleNavigation}
          onSignOut={() => signOut()}
        />
        <DashboardSidebar user={user} isCollapsed={isSidebarCollapsed} onNavigate={handleNavigation} />

        <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
          <div className="p-4 lg:p-6 max-w-[1600px] mx-auto">
            {error && (
              <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-1">
                  Bine ai revenit, {user.name}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {new Date().toLocaleDateString('ro-RO', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                {me?.school_name && (
                  <p className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>
                      Școala: <span className="font-medium text-foreground">{me.school_name}</span>
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <span className="text-muted-foreground">Rating</span>
                      <span className="font-medium text-foreground">
                        {typeof schoolRating?.ratingPercent === 'number' ? `${schoolRating.ratingPercent}%` : '—'}
                      </span>
                      <Icon
                        name={(schoolRating?.ratingTrend || 'flat') === 'up' ? 'TrendingUp' : (schoolRating?.ratingTrend || 'flat') === 'down' ? 'TrendingDown' : 'Minus'}
                        size={16}
                        className={
                          typeof schoolRating?.ratingDelta === 'number'
                            ? (schoolRating?.ratingTrend || 'flat') === 'up'
                              ? 'text-success'
                              : (schoolRating?.ratingTrend || 'flat') === 'down'
                                ? 'text-error'
                                : 'text-muted-foreground'
                            : 'text-muted-foreground'
                        }
                        title={
                          typeof schoolRating?.ratingDelta === 'number'
                            ? (schoolRating?.ratingTrend || 'flat') === 'up'
                              ? `În urcare (+${schoolRating.ratingDelta} pp, ultimele ${schoolRating.ratingTrendWindowDays ?? 30} zile)`
                              : (schoolRating?.ratingTrend || 'flat') === 'down'
                                ? `În coborâre (${schoolRating.ratingDelta} pp, ultimele ${schoolRating.ratingTrendWindowDays ?? 30} zile)`
                                : `Stagnare (0 pp, ultimele ${schoolRating.ratingTrendWindowDays ?? 30} zile)`
                            : `Nu există date anterioare pentru comparație (ultimele ${schoolRating?.ratingTrendWindowDays ?? 30} zile).`
                        }
                      />
                    </span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <SharedToolsMenu user={user} onNavigate={handleNavigation} />
                <CommunicationNotifier user={user} messages={messages} onNavigate={handleNavigation} onMarkAsRead={() => {}} />
                <Button variant="outline" iconName="FileText" iconPosition="left" onClick={() => navigate('/documents')}>
                  <span className="hidden sm:inline">Documente</span>
                  <span className="sm:hidden">Doc</span>
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  disabled={isB2B}
                  onClick={() => {
                    if (isB2B) return;
                    navigate('/class-management-hub?action=create');
                  }}
                >
                  <span className="hidden sm:inline">Creează clasă</span>
                  <span className="sm:hidden">Creează</span>
                </Button>
              </div>
            </div>

            <div className="mb-6">
              <QuickStats stats={quickStats} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-foreground">Clasele mele</h2>
                    <Button variant="ghost" size="sm" iconName="Grid3x3" onClick={() => navigate('/class-management-hub')}>
                      Vezi toate
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {classes.slice(0, 4).map((classData) => (
                      <ClassCard key={classData.id} classData={classData} />
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <PendingRequestsCard requests={pendingRequests} onRefresh={loadDashboard} />
                </div>
              </div>

              <div className="space-y-6">
                <TodaySchedule lessons={todayLessons} />
                <NotificationsCard
                  notifications={notifications}
                  onMarkAllRead={async () => {
                    await markAllNotificationsRead();
                    await loadDashboard();
                  }}
                />
                <AppointmentsCard />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Icon name="Lightbulb" size={20} className="text-accent" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-foreground mb-1">Sfat rapid</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Configurează orarul din „Gestionare clase → Orar”, apoi îl vei vedea aici pe panou.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="left"
                    onClick={() => navigate('/class-management-hub?tab=orar')}
                  >
                    Gestionează orarul
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default TeacherDashboard;
